import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Input, Label } from "@/components/ui/Input";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

import {
  DEFAULT_QUOTE_SETTINGS,
  fetchQuoteSettings,
  patchQuoteSettings,
  type QuoteSettings,
} from "./insideSalesQuotesApi";

const QUERY_KEY = ["inside-sales", "quotes", "settings"] as const;

export function QuoteSettingsSection() {
  const queryClient = useQueryClient();
  const canManage = useHubModuleAdmin("inside_sales");
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: fetchQuoteSettings });
  const [draft, setDraft] = useState<QuoteSettings>(DEFAULT_QUOTE_SETTINGS);

  useEffect(() => {
    if (query.data) setDraft(query.data);
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () => patchQuoteSettings(draft),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });
  const dirty = query.data != null && (
    draft.priceLimitMin !== query.data.priceLimitMin
    || draft.priceLimitMax !== query.data.priceLimitMax
    || draft.amountLimit !== query.data.amountLimit
    || draft.talkLimit !== query.data.talkLimit
  );
  const field = (key: keyof Pick<QuoteSettings, "priceLimitMin" | "priceLimitMax" | "amountLimit" | "talkLimit">, label: string, step: string) => (
    <div>
      <Label htmlFor={`quote-setting-${key}`}>{label}</Label>
      <Input
        id={`quote-setting-${key}`}
        type="number"
        min="0"
        step={step}
        value={draft[key]}
        disabled={!canManage}
        onChange={(event) => setDraft((current) => ({ ...current, [key]: Number(event.target.value) }))}
        className="mt-1 h-8"
      />
    </div>
  );

  return (
    <HubSettingsSection
      title="Quotes"
      hoverHint="Controls allowed catalog price adjustments, the advisory total, and the total requiring approval before PDF download."
    >
      <div className="grid max-w-3xl gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {field("priceLimitMin", "Minimum price multiplier", "0.01")}
        {field("priceLimitMax", "Maximum price multiplier", "0.01")}
        {field("talkLimit", "Talk-to-manager warning", "100")}
        {field("amountLimit", "Approval required for download", "100")}
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          {mutation.isError ? (mutation.error instanceof Error ? mutation.error.message : "Could not save quote settings.") : "Multipliers apply to catalog tier prices; administrators may override them."}
        </p>
        <HubSaveButton
          type="button"
          dirty={dirty}
          disabled={!canManage}
          isLoading={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          Save quote settings
        </HubSaveButton>
      </div>
    </HubSettingsSection>
  );
}
